/**
 * RAPIDCONTACT MAPPERS — conversione tra tipi UI e DTO REST.
 *
 * Pattern di riferimento: personMappers.ts. Versione semplificata:
 * - 16 campi scalari, nessun enum int<->string,
 * - nessun dual-field (no source/channel a find-or-create),
 * - nessuna collezione nested (email/phone sono scalari, non liste),
 * - mapper sono quasi identity (l'unica logica e' la normalizzazione di
 *   String null/undefined dal wire format alla shape UI).
 *
 * Fonte di verita' dei types RapidContact: `RapidContact`, `RapidContactDto`,
 * `RapidContactInput` vivono qui. rapidContactTypes li re-esporta e aggiunge
 * i tipi UI-centrici (RapidContactState, payload create/update).
 */

import { pickAuditFields, type AuditFields } from '@crm/shared';

// ─── DTO completo del RapidContact (shape REST, identica alla UI) ──────

/**
 * Shape del RapidContact come esce dal REST (/o/crm/v1_0/rapid-contact/{id} e
 * nested in /rapid-contacts/{groupId}). Rispecchia schema RapidContact del
 * rest-openapi.yaml. Tutti campi scalari, no nested. Usato solo da
 * rapidContactApi/rapidContactMappers.
 */
export interface RapidContactDto {
  rapidContactId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  companyName: string | null;
  companyDomain: string | null;
  companyAddress: string | null;
  companyCountry: string | null;
  fairEvent: string | null;
  interest: string | null;
  productCategory: string | null;
  /** 0 = unset, 1-5 valori validi. */
  interestLevel: number;
  followupAction: string | null;
  /** Stato di follow-up, la key di una voce della picklist CRM_RAPID_CONTACT_FOLLOWUP_STATUS. */
  followupStatusListTypeEntryKey: string | null;
  /** Nome della voce nella lingua della richiesta (read-only, dal server); la key se la voce non c'è più. */
  followupStatusName: string | null;
  notes: string | null;
  /** Commerciale di riferimento (FILO-67): un utente del sito, sempre valorizzato. */
  salespersonUserId: number;
  /** Nome del commerciale (read-only, dal server): serve a mostrare anche un utente disattivato. */
  salespersonUserName: string;
  /** Stato di import (read-only): true = già importato nel sistema (Person collegato). */
  imported: boolean;
}

// ─── Shape UI del RapidContact ─────────────────────────────────────────

export interface RapidContact extends AuditFields {
  rapidContactId: number;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  companyName: string | null;
  companyDomain: string | null;
  companyAddress: string | null;
  companyCountry: string | null;
  fairEvent: string | null;
  interest: string | null;
  productCategory: string | null;
  /** 0 = unset, 1-5 valori validi. */
  interestLevel: number;
  followupAction: string | null;
  /** Stato di follow-up, la key di una voce della picklist CRM_RAPID_CONTACT_FOLLOWUP_STATUS. */
  followupStatusListTypeEntryKey: string | null;
  /** Nome della voce nella lingua della richiesta (read-only, dal server); la key se la voce non c'è più. */
  followupStatusName: string | null;
  notes: string | null;
  /** Commerciale di riferimento (FILO-67): un utente del sito, sempre valorizzato. */
  salespersonUserId: number;
  /** Nome del commerciale (read-only, dal server): serve a mostrare anche un utente disattivato. */
  salespersonUserName: string;
  /** Stato di import (read-only): true = già importato nel sistema (Person collegato). */
  imported: boolean;
}

// ─── DTO di input (POST /rapid-contacts/{groupId} e PATCH /rapid-contact/{id}) ──

/**
 * Shape del payload che il backend accetta in create/update RapidContact.
 * Allineato a RapidContactInput in rest-openapi.yaml: include solo i campi
 * modificabili via REST; esclude audit (server-driven) e rapidContactId (path).
 * Tutti i campi sono opzionali (PATCH-friendly). firstName e lastName sono
 * obbligatori in create — la mancanza viene gestita dal backend (validate)
 * e dal client a livello di form, non dal type system.
 */
export interface RapidContactInput {
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  companyName?: string | null;
  companyDomain?: string | null;
  companyAddress?: string | null;
  companyCountry?: string | null;
  fairEvent?: string | null;
  interest?: string | null;
  productCategory?: string | null;
  interestLevel?: number;
  followupAction?: string | null;
  /** Key di una voce della picklist; nel PATCH, se assente, resta quella attuale. */
  followupStatusListTypeEntryKey?: string | null;
  notes?: string | null;
  /** Obbligatorio in create (FILO-67); nel PATCH, se assente, resta quello attuale. */
  salespersonUserId?: number;
}

// ─── Whole-DTO mappers ─────────────────────────────────────────────────

/**
 * Modello UI -> DTO REST. Usato dai mock MSW per simulare esattamente la
 * shape che il backend emette. In produzione, chi invia un RapidContact al
 * backend passa per `rapidContactCreateToInput` / `rapidContactUpdateToInput`
 * — questo whole-DTO converter non viene chiamato dall'app reale, solo dal
 * mock storage.
 */
export function rapidContactToDto(r: RapidContact): RapidContactDto {
  return {
    rapidContactId: r.rapidContactId,
    ...pickAuditFields(r),
    firstName: r.firstName,
    lastName: r.lastName,
    email: r.email,
    phone: r.phone,
    role: r.role,
    companyName: r.companyName,
    companyDomain: r.companyDomain,
    companyAddress: r.companyAddress,
    companyCountry: r.companyCountry,
    fairEvent: r.fairEvent,
    interest: r.interest,
    productCategory: r.productCategory,
    interestLevel: r.interestLevel,
    followupAction: r.followupAction,
    followupStatusListTypeEntryKey: r.followupStatusListTypeEntryKey,
    followupStatusName: r.followupStatusName,
    notes: r.notes,
    salespersonUserId: r.salespersonUserId,
    salespersonUserName: r.salespersonUserName,
    imported: r.imported,
  };
}

/**
 * DTO REST -> modello UI. Usato su GET / response POST / response PATCH.
 * Normalizza String null/undefined dal wire format (Liferay puo' emettere
 * String null come "" su alcuni endpoint legacy; qui li normalizziamo
 * sempre a null per coerenza UI).
 */
export function rapidContactFromDto(dto: RapidContactDto): RapidContact {
  return {
    rapidContactId: dto.rapidContactId,
    ...pickAuditFields(dto),
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email ?? null,
    phone: dto.phone ?? null,
    role: dto.role ?? null,
    companyName: dto.companyName ?? null,
    companyDomain: dto.companyDomain ?? null,
    companyAddress: dto.companyAddress ?? null,
    companyCountry: dto.companyCountry ?? null,
    fairEvent: dto.fairEvent ?? null,
    interest: dto.interest ?? null,
    productCategory: dto.productCategory ?? null,
    interestLevel: dto.interestLevel ?? 0,
    followupAction: dto.followupAction ?? null,
    followupStatusListTypeEntryKey: dto.followupStatusListTypeEntryKey ?? null,
    followupStatusName: dto.followupStatusName ?? null,
    notes: dto.notes ?? null,
    salespersonUserId: dto.salespersonUserId ?? 0,
    salespersonUserName: dto.salespersonUserName ?? '',
    // Fail-safe (fail-closed): se il backend NON riporta `imported` (es. modulo REST
    // non ancora deployato, response parziale), lo consideriamo IMPORTATO così la form
    // di dettaglio si apre in sola lettura. Meglio bloccare un edit lecito che
    // consentire la modifica di un contatto già importato.
    imported: dto.imported ?? true,
  };
}

/**
 * DTO input (PATCH body) -> Partial<RapidContact> UI. Usato dai mock MSW
 * per applicare il patch al mockRapidContacts storage (che tiene la shape UI).
 */
export function rapidContactInputFromDto(input: RapidContactInput): Partial<RapidContact> {
  const out: Partial<RapidContact> = {};
  if (input.firstName !== undefined) out.firstName = input.firstName;
  if (input.lastName !== undefined) out.lastName = input.lastName;
  if (input.email !== undefined) out.email = input.email;
  if (input.phone !== undefined) out.phone = input.phone;
  if (input.role !== undefined) out.role = input.role;
  if (input.companyName !== undefined) out.companyName = input.companyName;
  if (input.companyDomain !== undefined) out.companyDomain = input.companyDomain;
  if (input.companyAddress !== undefined) out.companyAddress = input.companyAddress;
  if (input.companyCountry !== undefined) out.companyCountry = input.companyCountry;
  if (input.fairEvent !== undefined) out.fairEvent = input.fairEvent;
  if (input.interest !== undefined) out.interest = input.interest;
  if (input.productCategory !== undefined) out.productCategory = input.productCategory;
  if (input.interestLevel !== undefined) out.interestLevel = input.interestLevel;
  if (input.followupAction !== undefined) out.followupAction = input.followupAction;
  if (input.followupStatusListTypeEntryKey !== undefined) out.followupStatusListTypeEntryKey = input.followupStatusListTypeEntryKey;
  if (input.notes !== undefined) out.notes = input.notes;
  if (input.salespersonUserId !== undefined) out.salespersonUserId = input.salespersonUserId;
  return out;
}

/** Payload create UI -> DTO input. firstName e lastName obbligatori dal form. */
export function rapidContactCreateToInput(p: {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  companyName?: string | null;
  companyDomain?: string | null;
  companyAddress?: string | null;
  companyCountry?: string | null;
  fairEvent?: string | null;
  interest?: string | null;
  productCategory?: string | null;
  interestLevel?: number;
  followupAction?: string | null;
  followupStatusListTypeEntryKey?: string | null;
  notes?: string | null;
  salespersonUserId?: number;
}): RapidContactInput {
  return {
    firstName: p.firstName,
    lastName: p.lastName,
    email: p.email ?? null,
    phone: p.phone ?? null,
    role: p.role ?? null,
    companyName: p.companyName ?? null,
    companyDomain: p.companyDomain ?? null,
    companyAddress: p.companyAddress ?? null,
    companyCountry: p.companyCountry ?? null,
    fairEvent: p.fairEvent ?? null,
    interest: p.interest ?? null,
    productCategory: p.productCategory ?? null,
    interestLevel: p.interestLevel ?? 0,
    followupAction: p.followupAction ?? null,
    followupStatusListTypeEntryKey: p.followupStatusListTypeEntryKey ?? null,
    notes: p.notes ?? null,
    salespersonUserId: p.salespersonUserId,
  };
}

/**
 * Payload update UI -> DTO input parziale (PATCH). Include solo i campi
 * effettivamente presenti nel payload (undefined -> non inviato): il
 * backend preserva i campi assenti. La distinzione undefined/null e'
 * preservata: `email: null` viene inviato (svuota), `email: undefined`
 * non viene serializzato (mantieni current).
 */
export function rapidContactUpdateToInput(p: {
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  companyName?: string | null;
  companyDomain?: string | null;
  companyAddress?: string | null;
  companyCountry?: string | null;
  fairEvent?: string | null;
  interest?: string | null;
  productCategory?: string | null;
  interestLevel?: number;
  followupAction?: string | null;
  followupStatusListTypeEntryKey?: string | null;
  notes?: string | null;
  salespersonUserId?: number;
}): RapidContactInput {
  const out: RapidContactInput = {};
  if (p.firstName !== undefined) out.firstName = p.firstName;
  if (p.lastName !== undefined) out.lastName = p.lastName;
  if (p.email !== undefined) out.email = p.email;
  if (p.phone !== undefined) out.phone = p.phone;
  if (p.role !== undefined) out.role = p.role;
  if (p.companyName !== undefined) out.companyName = p.companyName;
  if (p.companyDomain !== undefined) out.companyDomain = p.companyDomain;
  if (p.companyAddress !== undefined) out.companyAddress = p.companyAddress;
  if (p.companyCountry !== undefined) out.companyCountry = p.companyCountry;
  if (p.fairEvent !== undefined) out.fairEvent = p.fairEvent;
  if (p.interest !== undefined) out.interest = p.interest;
  if (p.productCategory !== undefined) out.productCategory = p.productCategory;
  if (p.interestLevel !== undefined) out.interestLevel = p.interestLevel;
  if (p.followupAction !== undefined) out.followupAction = p.followupAction;
  if (p.followupStatusListTypeEntryKey !== undefined) out.followupStatusListTypeEntryKey = p.followupStatusListTypeEntryKey;
  if (p.notes !== undefined) out.notes = p.notes;
  if (p.salespersonUserId !== undefined) out.salespersonUserId = p.salespersonUserId;
  return out;
}
